import React, {useMemo} from "react";
import {ListItemText, ListSubheader, Radio} from "@mui/material";
import {Box, Checkbox, Chip, Colors, Text} from "@bounce/shared/ui";
import {MenuItem, Select} from "../../ui/components";
import {IconRadio, IconRadioChecked} from "../../assets";
import {ReachSegment} from "../../apis/requests/templates/types";
import {isEmptyCategoryFallback, usePopulatedSegmentIds} from "../segments/usePopulatedSegmentIds";
import {SEGMENT_CATEGORY_INFO} from "../segments/segment-form/constants";
import {categoryBucketLabel} from "../segments/segment-form/helpers";
import {SegmentCategoryMarker} from "../segments/SegmentCategoryMarker";
import {SegmentCategory} from "../../apis/requests/segments/types";
import {
  SegmentTagValue,
  allBucketsValue,
  findSelectedExclusiveId,
  groupSegmentTags,
  nextSegmentSelection,
  selectableSegments,
} from "./segmentTagSelection";

const SELECTION_CONTROL_SX = {margin: "2px 12px 2px 6px", padding: 0};
const CATEGORY_HINT = "all buckets, or pick one";
const ALL_BUCKETS_LABEL = "All buckets";
const ALL_BUCKETS_SECONDARY = "No tag needed";
const NO_ACCOUNTS_SECONDARY = "No accounts right now";

const renderRadioOption = (
  key: React.Key,
  value: SegmentTagValue,
  label: React.ReactNode,
  checked: boolean,
  disabled: boolean,
  secondary?: string,
  muted = false
) => (
  <MenuItem key={key} value={value} disabled={disabled}>
    <Radio
      size="small"
      checked={checked}
      icon={<IconRadio width={16} height={16} color={Colors.grey._700} />}
      checkedIcon={<IconRadioChecked width={16} height={16} color={Colors.bw.black} />}
      sx={SELECTION_CONTROL_SX}
    />
    <ListItemText primary={label} secondary={secondary} sx={{color: muted ? Colors.grey._500 : Colors.bw.black}} />
  </MenuItem>
);

const renderRadioItem = (segment: ReachSegment, checked: boolean, disabled: boolean, muted = false) =>
  renderRadioOption(
    segment.id,
    segment.id,
    categoryBucketLabel(segment),
    checked,
    disabled,
    muted ? NO_ACCOUNTS_SECONDARY : undefined,
    muted
  );

type Props = {
  segments: ReachSegment[];
  value: number[];
  onChange: (segmentIds: number[]) => void;
  validationOnly: boolean;
};

const GroupHeader = ({
  title,
  hint,
  color,
  segmentCategory,
}: {
  title: string;
  hint: string;
  color: string;
  segmentCategory?: SegmentCategory;
}) => (
  <ListSubheader sx={{lineHeight: "32px", backgroundColor: Colors.tertiary._100}}>
    {segmentCategory && (
      <Box component="span" sx={{display: "inline-flex", mr: 1, verticalAlign: "middle"}}>
        <SegmentCategoryMarker segmentCategory={segmentCategory} />
      </Box>
    )}
    <Box
      component="span"
      sx={{color, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em"}}
    >
      {title}
    </Box>
    <Box component="span" sx={{color: Colors.grey._500, fontSize: 12, ml: 1}}>
      — {hint}
    </Box>
  </ListSubheader>
);

const SegmentTagsSelect = ({segments, value, onChange, validationOnly}: Props) => {
  const groups = useMemo(() => groupSegmentTags(segments, validationOnly), [segments, validationOnly]);
  const populatedSegmentIds = usePopulatedSegmentIds();
  const isEmptyFallback = (segment: ReachSegment) => isEmptyCategoryFallback(segment, populatedSegmentIds);
  const segmentById = useMemo(
    () => new Map(selectableSegments(groups).map((segment) => [segment.id, segment])),
    [groups]
  );
  const selectedExclusiveId = findSelectedExclusiveId(value, groups);
  const hasGeneralSelected = value.some((id) => id !== selectedExclusiveId && segmentById.has(id));
  const generalDisabled = selectedExclusiveId !== undefined;
  const showGeneralHeader = groups.general.length > 0 && (groups.exclusive.length > 0 || groups.categories.length > 0);

  return (
    <Select
      multiple
      fullWidth
      size="small"
      displayEmpty
      value={value}
      onChange={(e) => onChange(nextSegmentSelection(value, e.target.value as SegmentTagValue[], groups))}
      renderValue={(selected) => {
        const ids = (selected as number[]).filter((id) => segmentById.has(id));
        if (ids.length === 0)
          return (
            <Text size={14} sx={{color: Colors.grey._400}}>
              Select segments
            </Text>
          );
        return (
          <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
            {ids.map((id) => {
              const segment = segmentById.get(id)!;
              if (segment.type === "EXCLUSIVE")
                return <Chip key={id} size_="small" variant_="info" label={`Exclusive · ${segment.name}`} />;
              return segment.type === "VALIDATION" ? (
                <Chip key={id} size_="small" variant_="inherit" label={`Validation · ${segment.name}`} />
              ) : (
                <Chip
                  key={id}
                  size_="small"
                  variant_={
                    segment.segment_category ? SEGMENT_CATEGORY_INFO[segment.segment_category].chipVariant : "blue"
                  }
                  label={
                    segment.segment_category
                      ? `${SEGMENT_CATEGORY_INFO[segment.segment_category].label} · ${categoryBucketLabel(segment)}`
                      : segment.name
                  }
                />
              );
            })}
          </Box>
        );
      }}
    >
      {groups.exclusive.length > 0 && (
        <GroupHeader title="Exclusive" hint="targets this segment alone" color={Colors.grey._700} />
      )}
      {groups.exclusive.map((segment) =>
        renderRadioItem(segment, selectedExclusiveId === segment.id, hasGeneralSelected)
      )}
      {groups.categories.flatMap((category) => [
        <GroupHeader
          key={`header-${category.segmentCategory}`}
          title={SEGMENT_CATEGORY_INFO[category.segmentCategory].label}
          hint={CATEGORY_HINT}
          color={Colors.grey._700}
          segmentCategory={category.segmentCategory}
        />,
        renderRadioOption(
          `all-${category.segmentCategory}`,
          allBucketsValue(category.segmentCategory),
          ALL_BUCKETS_LABEL,
          !category.segments.some((segment) => value.includes(segment.id)),
          generalDisabled,
          ALL_BUCKETS_SECONDARY
        ),
        ...category.segments.map((segment) =>
          renderRadioItem(segment, value.includes(segment.id), generalDisabled, isEmptyFallback(segment))
        ),
      ])}
      {showGeneralHeader && <GroupHeader title="General" hint="can be combined" color={Colors.grey._700} />}
      {groups.general.map((segment) => (
        <MenuItem key={segment.id} value={segment.id} disabled={generalDisabled}>
          <Checkbox checked={value.includes(segment.id)} sx={SELECTION_CONTROL_SX} />
          <ListItemText primary={segment.name} />
        </MenuItem>
      ))}
      {groups.validation.map((segment) => (
        <MenuItem key={segment.id} value={segment.id}>
          <Checkbox checked={value.includes(segment.id)} sx={SELECTION_CONTROL_SX} />
          <ListItemText primary={segment.name} />
        </MenuItem>
      ))}
    </Select>
  );
};

export default SegmentTagsSelect;
